// features/notifications/NotificationBell.tsx — the topbar bell (contract §6k).
//
// One list of everything waiting on the signed-in user at this branch: drug
// substitutions to approve, ED pages, unacknowledged critical results, low or
// expired stock, consents missing on open visits. The server computes it from
// the records themselves, so an item leaves the list when its record is
// resolved — marking it read only clears it from the count.
//
// Opening the list does not mark anything read: a critical result must not
// stop counting just because the list was glanced at. Clicking an item marks
// it read and opens the screen that resolves it; "Mark all read" is explicit.
//
// It does NOT replace the ED DoctorAlertPopup, which still interrupts on every
// screen. The bell lists the same page so it can be found again.
import { useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { EmptyState, LoadingState } from '@/components/ui/States';
import { cn } from '@/lib/cn';
import { SubstitutionApprovalsDialog } from '@/features/pharmacy/SubstitutionApprovalsDialog';
import { useMarkNotificationsRead, useStaffNotifications } from './useStaffNotifications';
import type { NotificationLink, NotificationSeverity, StaffNotification } from './types';

const SEVERITY_DOT: Record<NotificationSeverity, string> = {
  critical: 'bg-danger',
  warning: 'bg-warn',
  info: 'bg-info',
};

const SOURCE_ICON: Record<StaffNotification['source_type'], string> = {
  pharmacy_substitution: '🔀',
  er_doctor_alert: '🚑',
  lab_critical: '🔬',
  stock_low: '💊',
  stock_expired: '⏳',
  transfer_in_transit: '🔄',
  indent_open: '📋',
  ward_med_request: '🏨',
  consent_due: '📝',
};

export function timeAgo(iso: string | null, now = Date.now()): string {
  if (!iso) return '';
  const mins = Math.round((now - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  return `${days} d ago`;
}

function NotificationRow({ item, onSelect }: { item: StaffNotification; onSelect: (item: StaffNotification) => void }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(item)}
      className={cn(
        'flex w-full items-start gap-2.5 rounded-[var(--radius)] px-2.5 py-2 text-left hover:bg-muted focus-visible:bg-muted focus-visible:outline-none',
        !item.read && 'bg-info-soft/40',
      )}
    >
      <span className="mt-0.5 text-[15px] leading-none" aria-hidden="true">{SOURCE_ICON[item.source_type]}</span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className={cn('h-2 w-2 shrink-0 rounded-full', SEVERITY_DOT[item.severity])} aria-hidden="true" />
          <span className={cn('truncate text-[13px]', item.read ? 'font-medium text-ink' : 'font-semibold text-ink')}>{item.title}</span>
        </span>
        {item.body && <span className="mt-0.5 block text-[12px] leading-snug text-ink-muted">{item.body}</span>}
        <span className="mt-0.5 block text-[11px] text-ink-muted">
          {timeAgo(item.occurred_at)}{!item.read && <span className="sr-only"> · unread</span>}
        </span>
      </span>
      {!item.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-teal" aria-hidden="true" />}
    </button>
  );
}

export function NotificationBell({ onNavigate }: {
  /** Opens the module that resolves a notification. */
  onNavigate: (link: NotificationLink) => void;
}) {
  const [open, setOpen] = useState(false);
  const [approvalsOpen, setApprovalsOpen] = useState(false);
  const feed = useStaffNotifications();
  const markRead = useMarkNotificationsRead();
  const unread = feed.data?.unread_count ?? 0;
  const items = feed.data?.items ?? [];

  const select = (item: StaffNotification) => {
    if (!item.read) markRead.mutate({ keys: [item.key] });
    setOpen(false);
    if (item.link.action === 'substitution_approvals') setApprovalsOpen(true);
    else onNavigate(item.link);
  };

  return (
    <>
      <Popover.Root open={open} onOpenChange={(o) => { setOpen(o); if (o) void feed.refetch(); }}>
        <Popover.Trigger asChild>
          <button
            type="button"
            aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
            title="Notifications"
            className="relative flex h-8 w-8 items-center justify-center rounded-full border border-line bg-card text-ink-muted hover:text-ink"
          >
            <Bell className="h-4 w-4" aria-hidden="true" />
            {unread > 0 && (
              <span className="absolute -right-1 -top-1 min-w-[18px] rounded-full bg-danger px-1 text-center text-[10px] font-bold leading-[18px] text-white">
                {unread > 99 ? '99+' : unread}
              </span>
            )}
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            align="end"
            sideOffset={8}
            collisionPadding={12}
            className="z-[210] flex max-h-[min(70vh,560px)] w-[min(380px,calc(100vw-24px))] flex-col rounded-md border border-border bg-card shadow-lg"
          >
            <div className="flex items-center justify-between border-b border-line px-3 py-2">
              <div className="text-[13px] font-semibold text-ink">
                Notifications{unread > 0 && <span className="ml-1.5 font-normal text-ink-muted">{unread} unread</span>}
              </div>
              <Button
                type="button" size="sm" variant="ghost"
                disabled={!unread || markRead.isPending}
                onClick={() => markRead.mutate({ all: true })}
              >
                Mark all read
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-1.5">
              {feed.isLoading ? (
                <LoadingState />
              ) : feed.isError ? (
                <EmptyState label="Notifications could not be loaded. They will retry shortly." />
              ) : !items.length ? (
                <EmptyState label="You're all caught up." />
              ) : (
                <div className="flex flex-col gap-0.5">
                  {items.map((item) => <NotificationRow key={item.key} item={item} onSelect={select} />)}
                </div>
              )}
            </div>
            <div className="border-t border-line px-3 py-1.5 text-[11px] text-ink-muted">
              Items leave this list once they are dealt with.
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      <SubstitutionApprovalsDialog open={approvalsOpen} onOpenChange={setApprovalsOpen} />
    </>
  );
}
